import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../app/queryKeys';
import { AccountTypeValue } from '../../types';
import { addUser, addStudent, addGuardian, deleteUser, editUser } from '../../services/UserService';

interface AddUserPayload {
    userType: AccountTypeValue;
    userDetails: Record<string, unknown>;
}

interface EditUserPayload {
    userId: string;
    userDetails: Record<string, unknown>;
}

export const useUserMutations = () => {
    const queryClient = useQueryClient();

    const invalidateUsers = () => queryClient.invalidateQueries({ queryKey: queryKeys.users.all });

    const addUserMutation = useMutation({
        mutationFn: ({ userType, userDetails }: AddUserPayload) => addUser(userDetails, userType),
    });

    const addStudentMutation = useMutation({
        mutationFn: (userDetails: Record<string, unknown>) => addStudent(userDetails),
    });

    const addGuardianMutation = useMutation({
        mutationFn: (userDetails: Record<string, unknown>) => addGuardian(userDetails),
    });

    const editUserMutation = useMutation({
        mutationFn: ({ userId, userDetails }: EditUserPayload) => editUser(userId, userDetails),
        onSuccess: (_data, variables) => {
            invalidateUsers();
            queryClient.invalidateQueries({
                queryKey: queryKeys.users.profile(variables.userId),
            });
        },
    });

    const deleteUserMutation = useMutation({
        mutationFn: (userId: string) => deleteUser(userId),
        onSuccess: invalidateUsers,
    });

    return {
        addUserMutation,
        addStudentMutation,
        addGuardianMutation,
        editUserMutation,
        deleteUserMutation,
    };
};
